import { AlertTriangle, Bell, Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '../../services/notificationApi';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
}

// Record<string, …> because notification.type is free-form (VARCHAR(50)):
// 'device_availability' is written today and 'schedule_event' arrives with
// CAL-14. Anything unmapped falls back to Bell below — mirrors SEVERITY_DOT.
const ICON_MAP: Record<string, LucideIcon> = {
  alarm: AlertTriangle,
  system: Bell,
  info: Info,
};

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-ems-red',
  high: 'bg-ems-amber',
  medium: 'bg-ems-amber',
  low: 'bg-ems-sky',
  info: 'bg-ems-text-muted',
};

export function NotificationItem({ notification, onMarkRead }: NotificationItemProps) {
  const Icon = ICON_MAP[notification.type] || Bell;
  const dotColor = SEVERITY_DOT[notification.severity] || SEVERITY_DOT.info;

  const handleClick = () => {
    if (!notification.read) {
      onMarkRead(notification.id);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-ems-bg-card-hover transition-colors ${
        !notification.read ? 'bg-ems-green/5' : ''
      }`}
    >
      <div className="relative flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-ems-text-muted" />
        {!notification.read && (
          <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${dotColor}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-tight ${!notification.read ? 'font-medium text-ems-text-primary' : 'text-ems-text-secondary'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-ems-text-muted mt-0.5 truncate">{notification.message}</p>
        <div className="flex items-center gap-2 mt-1">
          {/* Site CODE (e.g. HONDO-001), not a display name: alarms.notifications has
              no site column at all — both dispatch writers put the wire site code in
              metadata.site_id. The typeof narrowing is required, not decorative:
              metadata is Record<string, unknown>. */}
          {typeof notification.metadata?.site_id === 'string' && (
            <span className="text-[10px] text-ems-text-muted bg-ems-bg px-1.5 py-0.5 rounded">
              {notification.metadata.site_id}
            </span>
          )}
          <span className="text-[10px] text-ems-text-muted">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </button>
  );
}
